"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { WhatsAppLink } from "./WhatsAppLink";
import { deriveLocaleFromPath, localizeHref } from "@/lib/i18n";
import { getMessages } from "@/lib/messages";

const LOGO_URL = "/cloudaxe_onlyLogo_Transparent_NoBuffer.png";

/**
 * Site-wide footer matching the homepage multi-column layout.
 * Hash links to home sections use localized `/#…` so they work from any route.
 */
export function SiteFooter() {
  const pathname = usePathname() || "/";
  const ctx = deriveLocaleFromPath(pathname);
  const m = getMessages(ctx.locale);

  return (
    <footer className="mt-20 border-t border-slate-200/70 pt-10 text-sm text-slate-500 dark:border-white/10 dark:text-white/60">
      <div className="grid gap-10 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
        <div>
          <div className="inline-flex items-center gap-2">
            <div className="logo-3d-wrap relative h-8 w-8 shrink-0 overflow-hidden">
              <div className="logo-3d-inner h-full w-full">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={LOGO_URL}
                  alt=""
                  width={32}
                  height={32}
                  className="h-full w-full object-contain"
                />
              </div>
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold tracking-wide text-slate-900 dark:text-white">
                Cloud<span className="text-slate-500 dark:text-white/70">Axe</span>
              </div>
              <div className="text-xs text-slate-500 dark:text-white/60">{m.footer.breakThru}</div>
            </div>
          </div>
          <p className="mt-4 max-w-xs text-xs leading-relaxed text-slate-500 dark:text-white/55">
            {m.footer.tagline}
          </p>
          <div className="mt-4 flex items-center gap-3">
            <a
              href="https://www.facebook.com/CloudAxeSupport"
              target="_blank"
              rel="noopener noreferrer me"
              aria-label="Follow CloudAxe on Facebook"
              title="Follow Us on Facebook!"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/50 ring-1 ring-slate-200/70 transition-colors hover:bg-slate-100 dark:bg-white/10 dark:ring-white/15 dark:hover:bg-white/15"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/icons/facebook.svg"
                alt=""
                width={16}
                height={16}
                className="h-4 w-4 object-contain"
              />
            </a>
            <a
              href="https://www.linkedin.com/company/cloudaxe-io/"
              target="_blank"
              rel="noopener noreferrer me"
              aria-label="Follow CloudAxe on LinkedIn"
              title="Follow Us on LinkedIn!"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/50 ring-1 ring-slate-200/70 transition-colors hover:bg-slate-100 dark:bg-white/10 dark:ring-white/15 dark:hover:bg-white/15"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/icons/linkedin.svg"
                alt=""
                width={16}
                height={16}
                className="h-4 w-4 object-contain"
              />
            </a>
            <WhatsAppLink
              label="Chat with CloudAxe on WhatsApp"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/50 ring-1 ring-slate-200/70 transition-colors hover:bg-slate-100 dark:bg-white/10 dark:ring-white/15 dark:hover:bg-white/15"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/icons/whatsapp.svg"
                alt=""
                width={16}
                height={16}
                className="h-4 w-4 object-contain"
              />
            </WhatsAppLink>
          </div>
        </div>

        <div>
          <div className="eyebrow text-slate-500 dark:text-white/55">{m.footer.services}</div>
          <ul className="mt-3 space-y-2 text-sm">
            <li>
              <Link className="hover:text-slate-900 dark:hover:text-white" href="/apps">
                {m.footer.apps}
              </Link>
            </li>
            <li>
              <Link className="hover:text-slate-900 dark:hover:text-white" href={localizeHref("/web-support", ctx)}>
                {m.footer.webSupport}
              </Link>
            </li>
            <li>
              <Link className="hover:text-slate-900 dark:hover:text-white" href={localizeHref("/hosting", ctx)}>
                {m.footer.hosting}
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <div className="eyebrow text-slate-500 dark:text-white/55">{m.footer.help}</div>
          <ul className="mt-3 space-y-2 text-sm">
            <li>
              <Link className="hover:text-slate-900 dark:hover:text-white" href={localizeHref("/help", ctx)}>
                {m.footer.helpCenter}
              </Link>
            </li>
            <li>
              <Link className="hover:text-slate-900 dark:hover:text-white" href={localizeHref("/help/faqs", ctx)}>
                {m.footer.faq}
              </Link>
            </li>
            <li>
              <Link className="hover:text-slate-900 dark:hover:text-white" href={localizeHref("/contact", ctx)}>
                {m.footer.contact}
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <div className="eyebrow text-slate-500 dark:text-white/55">{m.footer.clients}</div>
          <ul className="mt-3 space-y-2 text-sm">
            <li>
              <a
                className="hover:text-slate-900 dark:hover:text-white"
                href="https://panel.cloudaxe.io"
                rel="noopener noreferrer"
                target="_blank"
              >
                {m.footer.clientPortal}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-slate-200/70 pt-6 text-xs dark:border-white/10">
        <div>
          © {new Date().getFullYear()} {m.footer.copyright}
        </div>
        <div className="flex gap-4">
          <Link className="hover:text-slate-900 dark:hover:text-white" href="/apps">
            {m.footer.apps}
          </Link>
          <Link className="hover:text-slate-900 dark:hover:text-white" href={localizeHref("/help/faqs", ctx)}>
            {m.footer.faq}
          </Link>
          <Link className="hover:text-slate-900 dark:hover:text-white" href={localizeHref("/contact", ctx)}>
            {m.footer.contact}
          </Link>
          <Link className="hover:text-slate-900 dark:hover:text-white" href="/privacy">
            {m.footer.privacy}
          </Link>
          <Link className="hover:text-slate-900 dark:hover:text-white" href="/terms">
            {m.footer.terms}
          </Link>
        </div>
      </div>
    </footer>
  );
}
